import { PcseEyeRx } from '../../@types/pcse';

/**
 * Read a stored refraction into the per-eye powers a GOS3 claim needs.
 *
 * `Refraction.final` is written by the clinical UI as flat, prefixed keys
 * (`distanceRightSPH`, `nearRightAdd`, …) holding **strings**, so every value is
 * parsed defensively: a blank field must read as absent rather than `NaN`, or
 * the voucher band silently collapses to plano and the practice under-claims.
 *
 * Mirrors `PcseClaimFromDispenseService#readPrescription` on the backend, which
 * does the same job for the dispense → claim route.
 */
export interface ReadPrescriptionResult {
  right: PcseEyeRx;
  left: PcseEyeRx;
  /** False when the patient has no refraction, or it holds no usable powers. */
  found: boolean;
}

const EMPTY: ReadPrescriptionResult = { right: {}, left: {}, found: false };

export function readPrescription(
  final?: Record<string, unknown> | null,
): ReadPrescriptionResult {
  if (!final) return EMPTY;

  const num = (key: string): number | undefined => {
    const raw = final[key];
    if (raw == null || raw === '') return undefined;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const eye = (side: 'Right' | 'Left'): PcseEyeRx => ({
    sphere: num(`distance${side}SPH`),
    cylinder: num(`distance${side}CYL`),
    axis: num(`distance${side}Axis`),
    add: num(`near${side}Add`),
    prism: num(`distance${side}Prism`),
  });

  const right = eye('Right');
  const left = eye('Left');

  // A refraction row that exists but has every power blank is not a usable
  // prescription — treat it as absent so the UI asks rather than assuming plano.
  const found = [right, left].some((e) =>
    [e.sphere, e.cylinder, e.add].some((v) => v != null),
  );

  return { right, left, found };
}

/** Human summary of an eye's powers, e.g. `-2.25 / -0.75 x 180`. */
export function describeEye(eye: PcseEyeRx): string {
  if (eye.sphere == null && eye.cylinder == null) return '—';
  const parts = [(eye.sphere ?? 0).toFixed(2)];
  if (eye.cylinder != null) {
    parts.push(`/ ${eye.cylinder.toFixed(2)}`);
    if (eye.axis != null) parts.push(`x ${eye.axis}`);
  }
  if (eye.add != null && eye.add !== 0) parts.push(`add ${eye.add.toFixed(2)}`);
  return parts.join(' ');
}
